/**
 * Phase 105-Agent-A — OnlineToggle.
 *
 * Header pill toggle wired to PUT /api/agents/status. Server is the
 * source of truth — we read `is_online` from /api/auth/me on mount and
 * mirror it locally. Toggling flips the server-side flag, after which
 * GET /api/agent/my-tasks honours the new state (returns `[]` when
 * offline).
 *
 * Sprint-105d submit-blocked-reason pattern applies — if the toggle is
 * mid-flight, hover surfaces "Updating status…" via title.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

export default function OnlineToggle({ onChange }) {
  const [isOnline, setIsOnline] = useState(null);   // null = unknown
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        // is_online may be undefined for legacy accounts; treat undefined as offline.
        setIsOnline(!!data?.is_online);
      } catch (e) {
        setIsOnline(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    if (busy || isOnline === null) return;
    const next = !isOnline;
    setBusy(true);
    // Optimistic update.
    setIsOnline(next);
    try {
      await api.put("/agents/status", { is_online: next });
      toast.success(next ? "You're online — pickups will appear." : "You're offline — pickups paused.");
      if (onChange) onChange(next);
    } catch (e) {
      // Roll back.
      setIsOnline(!next);
      toast.error("Couldn't update your status — try again.");
    } finally {
      setBusy(false);
    }
  };

  const label = isOnline === null ? "Loading…" : isOnline ? "Online" : "Offline";
  const blockedReason = isOnline === null
    ? "Loading your status…"
    : busy
    ? "Updating status…"
    : null;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy || isOnline === null}
      title={blockedReason || (isOnline ? "Tap to go offline" : "Tap to go online")}
      aria-disabled={busy || isOnline === null}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        isOnline === null
          ? "bg-slate-200 text-slate-500 cursor-wait"
          : isOnline
          ? "bg-emerald-500 text-white hover:bg-emerald-600 focus:ring-emerald-400"
          : "bg-slate-300 text-slate-700 hover:bg-slate-400 focus:ring-slate-400"
      }`}
      data-testid="agent-online-toggle"
      data-state={isOnline ? "online" : "offline"}
    >
      <span
        className={`w-2 h-2 rounded-full ${
          isOnline === null ? "bg-slate-400" : isOnline ? "bg-white animate-pulse" : "bg-slate-500"
        }`}
        aria-hidden
      />
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
      {label}
    </button>
  );
}
